import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import ChainedBackend from 'i18next-chained-backend';
import LocalStorageBackend from 'i18next-localstorage-backend';

import translationEN from '../locales/en.json';
import translationSV from '../locales/sv.json';
import translationES from '../locales/es.json';
import translationNB from '../locales/nb.json';
import translationNL from '../locales/nl.json';
import translationDE from '../locales/de.json';
import translationFI from '../locales/fi.json';
import translationDA from '../locales/da.json';
import translationHE from '../locales/he.json';
import { config } from '../utils/config';
import apis from '../utils/apis';

// Default manifest values - app will start with these if manifest fetch fails
const DEFAULT_MANIFEST = {
  version: '1',
  languages: ['en', 'sv', 'es', 'nb', 'nl', 'de', 'fi', 'da', 'he'],
  namespaces: ['translation'],
  defaultLocale: 'en'
};

// Fetch manifest in the background - non-blocking
async function fetchManifest() {
  try {
    const resp = await apis({
      method: "GET",
      url: "/i18n/manifest",
      timeout: 5000
    });
    return resp.data || DEFAULT_MANIFEST;
  } catch (error) {
    console.warn('Failed to fetch i18n manifest, using defaults:', error);
    return DEFAULT_MANIFEST;
  }
}

export async function initI18n() {
  // Use DEFAULT_MANIFEST immediately for app startup
  const manifest = DEFAULT_MANIFEST;

  const version = String(manifest.version || '1');
  const supportedLngs = manifest.languages || ['en'];
  const namespaces = manifest.namespaces || ['translation'];
  const fallbackLng = manifest.defaultLocale || 'en';
  const storedLanguage = localStorage.getItem('language') || fallbackLng;

  await i18n
    .use(ChainedBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      lng: storedLanguage,
      fallbackLng,
      supportedLngs,
      ns: namespaces,
      defaultNS: 'translation',
      load: 'currentOnly',
      backend: {
        backends: [LocalStorageBackend, HttpBackend],
        backendOptions: [
          {
            expirationTime: 7 * 24 * 60 * 60 * 1000,
            versions: supportedLngs.reduce((acc: any, lng: string) => {
              acc[lng] = version;
              return acc;
            }, {})
          },
          {
            loadPath: `${config.baseUrl}/i18n/{{lng}}/{{ns}}.json`,
            requestOptions: { credentials: 'include' }
          }
        ]
      },
      resources: {
        en: {
          translation: translationEN,
        },
        sv: {
          translation: translationSV,
        },
        es: {
          translation: translationES,
        },
        nb: {
          translation: translationNB,
        },
        nl: {
          translation: translationNL,
        },
        de: {
          translation: translationDE,
        },
        fi: {
          translation: translationFI,
        },
        da: {
          translation: translationDA,
        },
        he: {
          translation: translationHE,
        },
      },
      interpolation: {
        escapeValue: false,
      },
      react: {
        useSuspense: true,
      },
      debug: false,
      detection: { order: ['navigator'], caches: [] }
    },
    (err, t) => {
      if (err) return 
      t('key');
    })

  i18n.on('initialized', (val) => {
  })
  i18n.on('languageChanged', (lng) => {
    localStorage.setItem('language', lng);
  });

  i18n.on('loaded', (loaded) => {
  });

  i18n.on('failedLoading', (lng, ns, msg) => {
  });

  // Fetch the manifest in the background (non-blocking) to update supported languages
  // fetchManifest().then(updatedManifest => {
  //   if (updatedManifest && updatedManifest.languages) {
  //     i18n.services.backendConnector.language = updatedManifest.languages;
  //   }
  // }).catch(() => {
  //   // Silently ignore errors - app already started with defaults
  // });

  return i18n;
}


export async function changeLanguage(lng: string) {

  await i18n.loadLanguages(lng);
  await i18n.reloadResources([lng], ['translation']);  

  await i18n.changeLanguage(lng);
}
